import React, { useCallback, useContext } from 'react'
import { interactionStudioAPIController } from 'lib/interactionStudioUtils'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { OfferPackageOptionView } from 'selectors/offerPageSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import TextButton from 'components/Luxkit/Button/TextButton'
import eases from 'styles/tools/eases'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import OfferBookButton from 'components/OfferPage/OfferBookButton'

import Group from 'components/utils/Group'
import Pane from 'components/Common/Pane'
import OfferCalenderDatePickerFooterPriceDetails from './OfferCalenderDatePickerFooterPriceDetails'

const FooterContainer = styled.div`
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  pointer-events: none;

  &.visible {
    position: sticky;
    bottom: 0;
    pointer-events: auto;
  }
`

const Footer = styled(Pane)`
  padding: ${rem(20)} ${rem(24)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid  ${props => props.theme.palette.neutral.six};
  z-index: 1;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

interface Props {
  show?: boolean;
  canBook?: boolean;
  offer: App.Offer;
  selectedDay?: App.CalendarDay;
  duration: number;
  relevantPackages: Array<OfferPackageOptionView>;
  selectedPackageView?: OfferPackageOptionView;
  interactionStudioData?: App.ISACheckoutData;
  filter: OfferPageFilterTypes;
  isAnyFlightsActive?: boolean;
}

function OfferCalendarDatePickerFooter(props: Props) {
  const {
    show,
    canBook,
    offer,
    selectedDay,
    duration,
    relevantPackages,
    selectedPackageView,
    interactionStudioData,
    filter,
    isAnyFlightsActive,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
    interactionStudioAPIController(filter, interactionStudioData)
  }, [dispatch, filter, interactionStudioData])

  const onBookNowButtonClick = useCallback(() => {
    interactionStudioAPIController(filter, interactionStudioData)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.BookNow)
  }, [filter, interactionStudioData, onUserAnalyticsAction])

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  return <FooterContainer className={cn({ visible: show })}>
    <Footer className={cn({ visible: show })}>
      <Group gap={24} direction="horizontal" horizontalAlign="space-between" fullWidth>
        <div>
          {selectedDay && <OfferCalenderDatePickerFooterPriceDetails
            offer={offer}
            duration={duration}
            selectedDay={selectedDay}
            isAnyFlightsActive={isAnyFlightsActive}
            isFromPrice={relevantPackages.length > 1}
          />}
        </div>
        <Group gap={8} direction="horizontal" verticalAlign="center">
          {!!selectedDay && <TextButton kind="tertiary" size="large" onClick={onResetDates}>
            Reset dates
          </TextButton>}
          {canBook && <OfferBookButton
            size="large"
            data-testid="offer-calendar-apply"
            data-buytype={isAnyFlightsActive ? 'flights' : undefined}
            pkg={selectedPackageView?.package ?? relevantPackages[0]?.package}
            onSubmit={onBookNowButtonClick}
          >
            Book now
          </OfferBookButton>}
          {!canBook && <TextButton
            kind="primary"
            size="large"
            data-testid="offer-calendar-apply"
            onClick={onContinue}
          >
            Continue
          </TextButton>}
        </Group>
      </Group>
    </Footer>
  </FooterContainer>
}

export default OfferCalendarDatePickerFooter
